import { useEffect, useMemo } from "react"
import { useController, useFormContext } from "react-hook-form"
import { useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import type { RadioGroupProps } from "./RadioGroup"
import { RadioGroup } from "./RadioGroup"

export interface RadioGroupFieldProps extends Omit<RadioGroupProps, "value"> {
	withoutPreselection?: boolean
}

export const RadioGroupField = (props: RadioGroupFieldProps) => {
	const { name, options, required, withoutPreselection, ...rest } = props

	const { formatMessage } = useIntl()
	const { control, trigger } = useFormContext()

	const rules = useMemo(() => {
		return {
			required: {
				message: formatMessage(formMessages.required),
				value: !!required,
			},
		}
	}, [required, formatMessage])

	const {
		field: { onChange, ref, value },
		fieldState: { error, isDirty },
		formState: { isSubmitted },
	} = useController({
		control,
		// For more information, see https://designsystem.porsche.com/v2/components/radio-button/usage#behavior
		defaultValue: !withoutPreselection && options.length > 0 ? options[0].value : undefined,
		name,
		// One option should always be pre-selected, except for cases where a pre-selection can lead to wrong assumptions.
		rules,
		shouldUnregister: true,
	})

	// Fields need to retrigger validation in order to apply changed rules, since the rules are cached inside react-hook-form.
	// See https://github.com/react-hook-form/react-hook-form/issues/1749 and https://github.com/react-hook-form/react-hook-form/issues/8310
	useEffect(() => {
		if (isDirty || isSubmitted) {
			trigger(name)
		}
	}, [rules, trigger, name, isDirty, isSubmitted])

	return (
		<RadioGroup
			{...rest}
			data-testid={`radiogroup-field_${name}`}
			required={!!required}
			options={options}
			name={name}
			value={value}
			ref={ref}
			error={error?.message}
			onChange={onChange}
		/>
	)
}
